import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { getCompletions } from "../store/slices/habitsSlice";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { DayOfWeek } from "../types";
import {
  formatDateToString,
  parseDateString,
  isHabitActiveForDate,
} from "../utils/dateUtils";
import AppSidebar from "./AppSidebar";
import { useTheme } from "../hooks/useTheme";

const HabitDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const { isDark, setTheme } = useTheme();
  const userProfile = useAppSelector((state) => state.user.profile);
  const initials = userProfile?.name
    ? userProfile.name
        .split(" ")
        .map((w: string) => w.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("")
    : (userProfile?.username?.charAt(0).toUpperCase() ?? "U");

  const handleThemeToggle = () => setTheme(isDark ? "light" : "dark");

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  const { items: habits, completions } = useAppSelector(
    (state) => state.habits,
  );
  const habit = useMemo(() => habits.find((h) => h.id === id), [habits, id]);

  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  // Fetch completions for the habit if not already loaded
  useEffect(() => {
    trackPageVisit("habit_details", {
      habit_id: id,
      habit_name: habit?.name,
    });

    if (id && habit) {
      dispatch(getCompletions(id));
    }
  }, [id, habit, dispatch]);

  // Get completions for this habit
  const habitCompletions = useMemo(() => {
    return completions.filter((c) => c.habitId === id);
  }, [completions, id]);

  // Get month dates with day-of-week alignment
  const monthDates = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();

    // Get the first day of the month and its day-of-week
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay();

    // Get the last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const totalDates = lastDay.getDate();

    // Create array with padding for empty cells
    const dates: (Date | null)[] = [];

    // Add empty cells for days before the month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      dates.push(null);
    }

    // Add all dates in the month
    for (let i = 1; i <= totalDates; i++) {
      dates.push(new Date(year, month, i));
    }

    return dates;
  }, [selectedMonth]);

  // Day labels
  const dayLabels: Record<DayOfWeek, string> = {
    sunday: t("habitDetails.days.sunday") || "Sunday",
    monday: t("habitDetails.days.monday") || "Monday",
    tuesday: t("habitDetails.days.tuesday") || "Tuesday",
    wednesday: t("habitDetails.days.wednesday") || "Wednesday",
    thursday: t("habitDetails.days.thursday") || "Thursday",
    friday: t("habitDetails.days.friday") || "Friday",
    saturday: t("habitDetails.days.saturday") || "Saturday",
  };

  // Check if completion exists for a date
  const isCompletedOnDate = useCallback(
    (date: Date): boolean => {
      const dateString = formatDateToString(date);
      const completion = habitCompletions.find((c) => c.date === dateString);
      return completion ? completion.completed : false;
    },
    [habitCompletions],
  );

  // Calculate habit score for a date range
  const calculateHabitScore = useCallback(
    (
      startDate: Date,
      endDate: Date,
    ): { completed: number; total: number; percentage: number } => {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(0, 0, 0, 0);

      let completed = 0;
      let total = 0;

      const currentDate = new Date(start);
      while (currentDate.getTime() <= end.getTime()) {
        if (isHabitActiveForDate(habit?.activeForDays, currentDate)) {
          total++;
          if (isCompletedOnDate(currentDate)) {
            completed++;
          }
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
      return { completed, total, percentage };
    },
    [habit?.activeForDays, isCompletedOnDate],
  );

  // Calculate scores for different periods
  const getScoreForPeriod = useCallback(
    (
      days: number,
    ): { completed: number; total: number; percentage: number } => {
      const endDate = new Date();
      endDate.setHours(0, 0, 0, 0);

      // Get the start date for this period
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      startDate.setDate(startDate.getDate() - days + 1);

      // Determine the actual start date (don't go before habit creation)
      let effectiveStartDate = startDate;
      if (habit && habit.createdAt) {
        const habitCreatedDate = parseDateString(habit.createdAt);
        habitCreatedDate.setHours(0, 0, 0, 0);

        // If the period start is before habit creation, start from creation date
        if (startDate.getTime() < habitCreatedDate.getTime()) {
          effectiveStartDate = new Date(habitCreatedDate);
        }
      }

      return calculateHabitScore(effectiveStartDate, endDate);
    },
    [habit, calculateHabitScore],
  );

  // Get all-time score
  const getScoreForAllTime = useCallback((): {
    completed: number;
    total: number;
    percentage: number;
  } => {
    if (!habit || !habit.createdAt) {
      return { completed: 0, total: 0, percentage: 0 };
    }

    const createdDate = parseDateString(habit.createdAt);
    createdDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setHours(0, 0, 0, 0);

    return calculateHabitScore(createdDate, endDate);
  }, [habit, calculateHabitScore]);

  // Calculate scores for different periods - memoized
  const scoreLastWeek = useMemo(
    () => getScoreForPeriod(7),
    [getScoreForPeriod],
  );
  const scoreLastMonth = useMemo(
    () => getScoreForPeriod(30),
    [getScoreForPeriod],
  );
  const score3Months = useMemo(
    () => getScoreForPeriod(90),
    [getScoreForPeriod],
  );
  const score6Months = useMemo(
    () => getScoreForPeriod(180),
    [getScoreForPeriod],
  );
  const scoreLastYear = useMemo(
    () => getScoreForPeriod(365),
    [getScoreForPeriod],
  );
  const scoreAllTime = useMemo(
    () => getScoreForAllTime(),
    [getScoreForAllTime],
  );

  // Navigate months
  const handlePreviousMonth = () => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() - 1);
    setSelectedMonth(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() + 1);
    setSelectedMonth(newDate);
  };

  const handleToggleCompletion = (date: Date) => {
    trackUserInteraction(
      "toggle_habit_completion",
      "habit_management",
      id || "",
    );
    // This would dispatch toggleHabitCompletion action in a full implementation
  };

  const handleBack = () => {
    navigate("/habits");
  };

  const handleEdit = () => {
    navigate(`/edit-habit/${id}`);
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getMonthRangeText = (): string => {
    return selectedMonth.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  };

  // Calculate days since habit was created
  const daysExisted = useMemo(() => {
    if (!habit || !habit.createdAt) return 0;
    const createdDate = parseDateString(habit.createdAt);
    const today = new Date();
    const diffTime = today.getTime() - createdDate.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }, [habit]);

  if (!habit) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center">
        <MetaHead
          title={t("habitDetails.title")}
          description={t("habitDetails.description")}
        />
        <div className="text-center px-4">
          <div className="w-14 h-14 bg-[#f5a623]/20 border border-[#f5a623]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🔥</span>
          </div>
          <h3 className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white mb-2">
            {t("habitDetails.notFound")}
          </h3>
          <button
            onClick={handleBack}
            className="mt-4 px-5 py-2.5 bg-[#f5a623] text-[#1a1300] text-[18px] font-semibold rounded-xl hover:opacity-90 transition-all"
          >
            ← {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title={`${habit.name} - ${t("habitDetails.title")}`}
        description={t("habitDetails.description")}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#f5a623]/20 border border-[#f5a623]/30 flex items-center justify-center transition-all hover:bg-[#f5a623]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#f5a623"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none truncate max-w-[150px]">
              {habit.name}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-[19px] flex items-center justify-center cursor-pointer transition-all"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#f5a623]/15 border border-[#f5a623]/25 flex items-center justify-center transition-all hover:bg-[#f5a623]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#f5a623"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white truncate">
                {habit.name}
              </h1>
            </div>
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] text-[17px] font-medium text-gray-600 dark:text-white/60 hover:bg-black/[0.04] dark:hover:bg-white/[0.07] transition-all"
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path
                  d="M11.5 2.5a2.121 2.121 0 013 3L5 15H2v-3L11.5 2.5z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t("habitDetails.edit")}
            </button>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-4xl mx-auto space-y-4 pb-8">
              {/* SECTION 0: Overview Strip */}
              <div className="rounded-2xl border border-[#f5a623]/20 dark:border-[#f5a623]/30 bg-gradient-to-br from-[#f5a623]/[0.07] to-transparent dark:from-[#f5a623]/[0.09] p-4 lg:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#f5a623]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[24px] leading-none">🔥</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[19px] font-semibold tracking-tight text-gray-900 dark:text-white truncate">
                        {habit.name}
                      </p>
                      <p className="text-[16px] text-gray-600 dark:text-white/45">
                        {t("habitDetails.currentStreak")}: {habit.streak}{" "}
                        {t("habitDetails.daysLabel")}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`text-[15px] font-semibold px-2.5 py-1 rounded-full shrink-0 ${habit.isActive ? "bg-[#22c67a]/20 text-[#22c67a] border border-[#22c67a]/30" : "bg-red-500/20 text-red-400 border border-red-500/30"}`}
                  >
                    {habit.isActive
                      ? t("habitDetails.active")
                      : t("habitDetails.inactive")}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2">
                  <div className="rounded-xl border border-black/[0.08] dark:border-white/[0.1] bg-white/[0.55] dark:bg-white/[0.04] px-3 py-2.5">
                    <p className="text-[13px] font-medium uppercase tracking-widest text-gray-400 dark:text-white/30">
                      {t("habitDetails.createdDate")}
                    </p>
                    <p className="text-[16px] font-semibold text-gray-900 dark:text-white mt-0.5">
                      {habit.createdAt
                        ? formatDate(parseDateString(habit.createdAt))
                        : "-"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-black/[0.08] dark:border-white/[0.1] bg-white/[0.55] dark:bg-white/[0.04] px-3 py-2.5">
                    <p className="text-[13px] font-medium uppercase tracking-widest text-gray-400 dark:text-white/30">
                      {t("habitDetails.bestStreak")}
                    </p>
                    <p className="text-[16px] font-semibold text-gray-900 dark:text-white mt-0.5">
                      {habit.bestStreak} {t("habitDetails.daysLabel")}
                    </p>
                  </div>
                  <div className="rounded-xl border border-black/[0.08] dark:border-white/[0.1] bg-white/[0.55] dark:bg-white/[0.04] px-3 py-2.5">
                    <p className="text-[13px] font-medium uppercase tracking-widest text-gray-400 dark:text-white/30">
                      {t("habitDetails.activeDays")}
                    </p>
                    <p className="text-[16px] font-semibold text-gray-900 dark:text-white mt-0.5">
                      {habit.activeForDays?.length || 0}
                    </p>
                  </div>
                  <div className="rounded-xl border border-black/[0.08] dark:border-white/[0.1] bg-white/[0.55] dark:bg-white/[0.04] px-3 py-2.5">
                    <p className="text-[13px] font-medium uppercase tracking-widest text-gray-400 dark:text-white/30">
                      {t("habitDetails.status")}
                    </p>
                    <p className="text-[16px] font-semibold text-gray-900 dark:text-white mt-0.5">
                      {habit.isPaused
                        ? t("habitDetails.paused")
                        : t("habitDetails.active")}
                    </p>
                  </div>
                </div>
              </div>

              {/* SECTION 1: Main Habit Info */}
              <div className="bg-white/[0.55] dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.08] rounded-2xl p-4 lg:p-5">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  {/* Current Streak */}
                  <div className="bg-[#f5a623]/10 border border-[#f5a623]/20 rounded-xl p-3 text-center">
                    <p className="text-[13px] font-medium uppercase tracking-widest text-[#f5a623]/80 mb-1">
                      {t("habitDetails.currentStreak")}
                    </p>
                    <p className="text-[26px] font-semibold tracking-tight text-[#f5a623]">
                      {habit.streak}
                    </p>
                    <p className="text-[14px] text-[#f5a623]/60">
                      {t("habitDetails.daysLabel")}
                    </p>
                  </div>
                  {/* Best Streak */}
                  <div className="bg-[#22c67a]/10 border border-[#22c67a]/20 rounded-xl p-3 text-center">
                    <p className="text-[13px] font-medium uppercase tracking-widest text-[#22c67a]/80 mb-1">
                      {t("habitDetails.bestStreak")}
                    </p>
                    <p className="text-[26px] font-semibold tracking-tight text-[#22c67a]">
                      {habit.bestStreak}
                    </p>
                    <p className="text-[14px] text-[#22c67a]/60">
                      {t("habitDetails.daysLabel")}
                    </p>
                  </div>
                  {/* Created */}
                  <div className="bg-[#3b5cde]/10 border border-[#3b5cde]/20 rounded-xl p-3 text-center">
                    <p className="text-[13px] font-medium uppercase tracking-widest text-[#3b5cde]/80 mb-1">
                      {t("habitDetails.createdDate")}
                    </p>
                    <p className="text-[17px] font-semibold text-[#3b5cde]">
                      {habit.createdAt
                        ? formatDate(parseDateString(habit.createdAt))
                        : "-"}
                    </p>
                  </div>
                  {/* Status */}
                  <div
                    className={`${habit.isActive ? "bg-[#22c67a]/10 border-[#22c67a]/20" : "bg-red-500/10 border-red-500/20"} border rounded-xl p-3 text-center`}
                  >
                    <p className="text-[13px] font-medium uppercase tracking-widest text-white/40 dark:text-white/40 text-gray-400 mb-1">
                      {t("habitDetails.status")}
                    </p>
                    <span
                      className={`text-[16px] font-semibold px-2 py-0.5 rounded-full ${habit.isActive ? "bg-[#22c67a]/20 text-[#22c67a]" : "bg-red-500/20 text-red-400"}`}
                    >
                      {habit.isActive
                        ? t("habitDetails.active")
                        : t("habitDetails.inactive")}
                    </span>
                  </div>
                </div>

                {/* Active Days */}
                {habit.activeForDays && habit.activeForDays.length > 0 && (
                  <div className="pt-3 border-t border-black/[0.06] dark:border-white/[0.07]">
                    <p className="text-[13px] font-medium uppercase tracking-widest text-gray-400 dark:text-white/25 mb-2">
                      {t("habitDetails.activeDays")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {habit.activeForDays.map((day) => (
                        <span
                          key={day}
                          className="px-2.5 py-1 bg-[#f5a623]/15 border border-[#f5a623]/25 text-[#f5a623] text-[15px] font-medium rounded-full"
                        >
                          {dayLabels[day]}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Paused Notice */}
                {habit.isPaused && (
                  <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center gap-2">
                    <span className="text-lg">⏸️</span>
                    <span className="text-[16px] font-medium text-yellow-600 dark:text-yellow-400">
                      {t("habitDetails.paused")}
                    </span>
                  </div>
                )}

                {/* Mobile Edit Button */}
                <div className="lg:hidden mt-3">
                  <button
                    onClick={handleEdit}
                    className="w-full py-2.5 rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] text-[17px] font-medium text-gray-600 dark:text-white/60 hover:bg-black/[0.04] dark:hover:bg-white/[0.07] transition-all"
                  >
                    ✏️ {t("habitDetails.edit")}
                  </button>
                </div>
              </div>

              {/* SECTION 2: Monthly Scorecard */}
              <div className="bg-white/[0.55] dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.08] rounded-2xl p-4 lg:p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-[19px] font-semibold tracking-tight text-gray-900 dark:text-white">
                    {t("habitDetails.monthlyScorecard")}
                  </h2>
                  <div className="flex items-center gap-2 bg-white/[0.05] dark:bg-white/[0.05] rounded-xl px-3 py-1.5 border border-black/[0.07] dark:border-white/[0.08]">
                    <button
                      onClick={handlePreviousMonth}
                      className="text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white text-lg px-1 cursor-pointer transition-colors"
                    >
                      ‹
                    </button>
                    <span className="text-[17px] font-medium text-gray-700 dark:text-white/80 min-w-[110px] text-center">
                      {getMonthRangeText()}
                    </span>
                    <button
                      onClick={handleNextMonth}
                      className="text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white text-lg px-1 cursor-pointer transition-colors"
                    >
                      ›
                    </button>
                  </div>
                </div>

                <div className="mb-3 rounded-lg border border-black/[0.06] dark:border-white/[0.08] bg-black/[0.02] dark:bg-white/[0.02] px-3 py-2 flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-[3px] bg-[#22c67a]/50 border border-[#22c67a]/40" />
                    <span className="text-[14px] text-gray-500 dark:text-white/35">
                      {t("habitDetails.daysCompleted")}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-[3px] bg-[#3b5cde]/60" />
                    <span className="text-[14px] text-gray-500 dark:text-white/35">
                      {t("habitDetails.createdDate")}
                    </span>
                  </div>
                </div>

                {/* Day of Week Headers */}
                <div className="grid grid-cols-7 gap-[3px] lg:gap-1.5 mb-1">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (d) => (
                      <div key={d} className="text-center">
                        <span className="text-[13px] lg:text-[14px] font-medium uppercase tracking-wider text-gray-400 dark:text-white/30">
                          {d}
                        </span>
                      </div>
                    ),
                  )}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-[3px] lg:gap-1.5">
                  {monthDates.map((date, index) => {
                    if (date === null) {
                      return (
                        <div key={`empty-${index}`} className="aspect-square" />
                      );
                    }
                    const dateString = formatDateToString(date);
                    const isActive = isHabitActiveForDate(
                      habit.activeForDays,
                      date,
                    );
                    const isCompleted = isCompletedOnDate(date);
                    const isToday =
                      dateString === formatDateToString(new Date());

                    return (
                      <div
                        key={dateString}
                        style={{ aspectRatio: "1/1.2" }}
                        className={`relative rounded-[9px] border flex flex-col overflow-hidden transition-all duration-150 ${
                          !isActive
                            ? "opacity-20 pointer-events-none bg-transparent border-transparent"
                            : isCompleted
                              ? "bg-[#22c67a]/15 border-[#22c67a]/30 cursor-pointer"
                              : isToday
                                ? "bg-[#3b5cde]/15 border-[#3b5cde] border-[1.5px]"
                                : "bg-white/[0.04] border-white/[0.06] dark:bg-white/[0.04] dark:border-white/[0.06] cursor-pointer hover:bg-white/[0.07]"
                        }`}
                        onClick={() => isActive && handleToggleCompletion(date)}
                      >
                        <span
                          className={`text-[14px] lg:text-[16px] font-medium leading-none p-1 ${
                            isToday
                              ? "text-[#7b9fff]"
                              : isCompleted
                                ? "text-[#22c67a]"
                                : "text-gray-400 dark:text-white/30"
                          }`}
                        >
                          {date.getDate()}
                        </span>
                        {isCompleted && (
                          <span className="mt-auto text-center text-[14px] pb-0.5">
                            ✓
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* SECTION 3: Scores */}
              <div className="bg-white/[0.55] dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.08] rounded-2xl p-4 lg:p-5">
                <h2 className="text-[19px] font-semibold tracking-tight text-gray-900 dark:text-white mb-4">
                  {t("habitDetails.habitScores")}
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Last 7 Days */}
                  <div className="bg-[#a33fe8]/10 border border-[#a33fe8]/20 rounded-xl p-4 text-center">
                    <p className="text-[13px] font-medium uppercase tracking-widest text-[#a33fe8]/80 mb-2">
                      {t("habitDetails.lastWeek")}
                    </p>
                    <p className="text-[28px] font-semibold tracking-tight text-[#a33fe8]">
                      {scoreLastWeek.percentage}%
                    </p>
                    <p className="text-[15px] text-[#a33fe8]/60 mt-1">
                      {scoreLastWeek.completed}/{scoreLastWeek.total}{" "}
                      {t("habitDetails.daysCompleted")}
                    </p>
                  </div>
                  {/* Last 30 Days */}
                  {daysExisted >= 7 && (
                    <div className="bg-[#3b5cde]/10 border border-[#3b5cde]/20 rounded-xl p-4 text-center">
                      <p className="text-[13px] font-medium uppercase tracking-widest text-[#3b5cde]/80 mb-2">
                        {t("habitDetails.lastMonth")}
                      </p>
                      <p className="text-[28px] font-semibold tracking-tight text-[#7b9fff]">
                        {scoreLastMonth.percentage}%
                      </p>
                      <p className="text-[15px] text-[#7b9fff]/60 mt-1">
                        {scoreLastMonth.completed}/{scoreLastMonth.total}{" "}
                        {t("habitDetails.daysCompleted")}
                      </p>
                    </div>
                  )}
                  {/* Last 3 Months */}
                  {daysExisted >= 30 && (
                    <div className="bg-[#22c67a]/10 border border-[#22c67a]/20 rounded-xl p-4 text-center">
                      <p className="text-[13px] font-medium uppercase tracking-widest text-[#22c67a]/80 mb-2">
                        {t("habitDetails.last3Months")}
                      </p>
                      <p className="text-[28px] font-semibold tracking-tight text-[#22c67a]">
                        {score3Months.percentage}%
                      </p>
                      <p className="text-[15px] text-[#22c67a]/60 mt-1">
                        {score3Months.completed}/{score3Months.total}{" "}
                        {t("habitDetails.daysCompleted")}
                      </p>
                    </div>
                  )}
                  {/* Last 6 Months */}
                  {daysExisted >= 90 && (
                    <div className="bg-[#f5a623]/10 border border-[#f5a623]/20 rounded-xl p-4 text-center">
                      <p className="text-[13px] font-medium uppercase tracking-widest text-[#f5a623]/80 mb-2">
                        {t("habitDetails.last6Months")}
                      </p>
                      <p className="text-[28px] font-semibold tracking-tight text-[#f5a623]">
                        {score6Months.percentage}%
                      </p>
                      <p className="text-[15px] text-[#f5a623]/60 mt-1">
                        {score6Months.completed}/{score6Months.total}{" "}
                        {t("habitDetails.daysCompleted")}
                      </p>
                    </div>
                  )}
                  {/* Last Year */}
                  {daysExisted >= 180 && (
                    <div className="bg-[#e87b3f]/10 border border-[#e87b3f]/20 rounded-xl p-4 text-center">
                      <p className="text-[13px] font-medium uppercase tracking-widest text-[#e87b3f]/80 mb-2">
                        {t("habitDetails.lastYear")}
                      </p>
                      <p className="text-[28px] font-semibold tracking-tight text-[#e87b3f]">
                        {scoreLastYear.percentage}%
                      </p>
                      <p className="text-[15px] text-[#e87b3f]/60 mt-1">
                        {scoreLastYear.completed}/{scoreLastYear.total}{" "}
                        {t("habitDetails.daysCompleted")}
                      </p>
                    </div>
                  )}
                  {/* All Time */}
                  {daysExisted >= 180 && (
                    <div className="bg-[#E23F5D]/10 border border-[#E23F5D]/20 rounded-xl p-4 text-center">
                      <p className="text-[13px] font-medium uppercase tracking-widest text-[#E23F5D]/80 mb-2">
                        {t("habitDetails.allTime")}
                      </p>
                      <p className="text-[28px] font-semibold tracking-tight text-[#E23F5D]">
                        {scoreAllTime.percentage}%
                      </p>
                      <p className="text-[15px] text-[#E23F5D]/60 mt-1">
                        {scoreAllTime.completed}/{scoreAllTime.total}{" "}
                        {t("habitDetails.daysCompleted")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* MOBILE BOTTOM NAV */}
          <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 border-t border-black/[0.07] dark:border-white/[0.08] backdrop-blur-sm">
            <div className="flex justify-around px-2 pt-2 pb-5">
              {mobileNavLinks.map(({ path, emoji, label }) => {
                const active = isNavActive(path);
                return (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className="flex flex-col items-center gap-1"
                  >
                    <span className="text-[22px]">{emoji}</span>
                    <span
                      className={`text-[13px] font-medium ${active ? "text-[#f5a623]" : "text-gray-400 dark:text-white/25"}`}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>
        </main>
      </div>
    </>
  );
};

export default HabitDetails;
